import {Injectable} from '@angular/core';
import {BeforeLoginService} from "./before-login.service";
import {ModalService} from "./modal.service";
import {AuthenticateService} from "./authenticate.service";
import {NgxSpinnerService} from "ngx-spinner";
import {environment} from "../../environments/environment";
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {AcknowledgmentDetailsList} from "../models/authentication-result.model";
import {HttpClient} from "@angular/common/http";
import {UserInfoService} from "./user-info.service";
import {TranslateService} from "@ngx-translate/core";
import { ErrorCodes } from "../shared/constants/error-codes.enum";

@Injectable({
  providedIn: "root",
})
export class ConsentService {
  requestConsentUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/get-consent/";

  private _userHasGivenConsent = new BehaviorSubject<boolean>(false);
  userHasGivenConsent$ = this._userHasGivenConsent.asObservable();

  fetchedConsentRefs: Consent;
  isConsentDataReceived: boolean;
  userHasGivenConsent: boolean;


  constructor(
    private beforeLoginService: BeforeLoginService,
    private modalService: ModalService,
    private authenticateService: AuthenticateService,
    private userInfoService: UserInfoService,
    private translate: TranslateService,
    private spinner: NgxSpinnerService,
    private http: HttpClient,
  ) {
  }

  async getRequestConsent(selectedConsents: string[], isPremiumService?: boolean) {
    isPremiumService = isPremiumService ?? false;
    const observable = this.http.get<Consent>(this.requestConsentUrl, {
      observe: 'response',
      params: {
        consentParams: selectedConsents.join(','),
        isPremiumService: isPremiumService,
      },
      withCredentials: true,
    });

    try {
      const response = await firstValueFrom(observable);
      if (!response.body.acknowledgment || response.body.acknowledgment.responseCode !== "ERROR") {
        this.fetchedConsentRefs = response.body;
        await this.authenticateService.getSessionContext(false);
      } else {
        const messageCode = response.body.acknowledgment.acknowledgmentDetailList ? response.body.acknowledgment.acknowledgmentDetailList[0].messageCode : this.translate.instant('errorBackup');
        this.modalService.showErrorModal(messageCode, ErrorCodes.ERR041, messageCode !== "NT-002");
      }
    } catch (error) {
      // console.log("error", error);
    }
    return Promise.resolve();
  }

  async checkIfHasConsents() {
    this.isConsentDataReceived = false;
    this.showSpinner();
    await this.getRequestConsent([]).then(() => this.handleReceivedConsents());
    this.storeConsentContext();
    await this.authenticateService.getSessionContext(false);
    return this.userHasGivenConsent;
  }

  handleReceivedConsents() {
    if (this.hasValidPurposeDeclarationAndUrl()) {
      this.modalService.showGiveConsentModal({});
      this.updateConsent(false);
    } else {
      this.updateConsent(true);
    }
    this.isConsentDataReceived = true;
    this.hideSpinner();
  }

  private hasValidPurposeDeclarationAndUrl() {
    const consentRefs = this.fetchedConsentRefs;
    const hasValidPurposeDeclaration = consentRefs.validPurposeDeclarationIds
      && consentRefs.validPurposeDeclarationIds[environment.consentService.prescriptionDeclarationId];
    const hasExcludedPurposeDeclaration = consentRefs.validPurposeDeclarationIds
      && consentRefs.validPurposeDeclarationIds[environment.consentService.insuranceDeclarationId];
    const hasUrl = consentRefs.returnedUrl && consentRefs.returnedUrl.url;

    return (hasValidPurposeDeclaration || hasUrl) && !hasExcludedPurposeDeclaration;
  }

  private storeConsentContext() {
    const newConsentString = JSON.stringify(this.fetchedConsentRefs);
    sessionStorage.setItem('ConsentContext', newConsentString);
  }

  updateConsent(status: boolean) {
    this.userHasGivenConsent = status;
    this._userHasGivenConsent.next(status);
  }

  updateReturnedUrl(newUrl: string) {
    this.fetchedConsentRefs.returnedUrl = {url: newUrl};
    this.storeConsentContext();
  }

  showSpinner() {
    this.spinner.show();
  }

  hideSpinner() {
    this.spinner.hide();
  }
}

export interface Consent {
  returnedUrl: { url: string } | null;
  validPurposeDeclarationIds: {
    [key: string]: string;
  };
  acknowledgment?: {
    acknowledgmentDetailList?: AcknowledgmentDetailsList[];
    responseCode: string;
  };
}
